import { ContainerWidthType } from "@gatsby-plugin-generic-page/fragments/containerSettings"
import type { SpacingBase } from "@gatsby-plugin-generic-page/helpers/spacing"

// Widths are expressed in rem (not px) to match HEL's `space()`, which emits rem
// so container widths scale with the user's browser font-size setting.
export const MAX_WIDTH_CLASS: Record<ContainerWidthType, string> = {
  [ContainerWidthType.TINY]: "max-w-[30rem]",
  [ContainerWidthType.SMALL]: "max-w-[48rem]",
  [ContainerWidthType.MEDIUM]: "max-w-[60.5rem]",
  [ContainerWidthType.DEFAULT]: "max-w-[82rem]",
  [ContainerWidthType.FULL]: "max-w-[110rem]",
}

export const MAX_WIDTH_CLASS_FROM_LARGE: Record<ContainerWidthType, string> = {
  [ContainerWidthType.TINY]: "lg:max-w-[30rem]",
  [ContainerWidthType.SMALL]: "lg:max-w-[48rem]",
  [ContainerWidthType.MEDIUM]: "lg:max-w-[60.5rem]",
  [ContainerWidthType.DEFAULT]: "lg:max-w-[82rem]",
  [ContainerWidthType.FULL]: "lg:max-w-[110rem]",
}

// Spacing is written as arbitrary rem values, never bare Tailwind numbers: UDS's
// `@theme` layer remaps `--spacing-0`…`--spacing-11` onto a non-linear scale
// (0, 4, 8, 16, 24, 32, 40, 48, 64, 80, 96, 128px), so in that range `p-{n}` is
// not `4px * n` — `px-4` rendered 24px, not the 16px HEL produces. Values are
// HEL's own in the rem its `space()` emits: gutter `space(pageGutterSpace)` =
// 16px, background `space(6)` = 48px (what the old `ContainerOrg` was handed via
// `backgroundVerticalSpace`). `p-0`/`py-0` are exempt — zero is zero either way.
//
// Each variant also publishes its horizontal padding as `--base-container-px`
// so children can bleed into it (e.g. a carousel whose tiles run on to the
// container's edge) without re-deriving which padding variant is active.
export const PADDING_NONE = "p-0 [--base-container-px:0px]"
export const PADDING_NONE_FROM_LARGE = "lg:p-0 lg:[--base-container-px:0px]"
export const PADDING_GUTTER = "px-[1rem] py-0 [--base-container-px:1rem]"
export const PADDING_GUTTER_FROM_LARGE =
  "lg:px-[1rem] lg:py-0 lg:[--base-container-px:1rem]"
export const PADDING_BACKGROUND = "p-[3rem] [--base-container-px:3rem]"
export const PADDING_BACKGROUND_FROM_LARGE =
  "lg:p-[3rem] lg:[--base-container-px:3rem]"

// Each row is `spacingSpace[key][small|medium|large] * 8` px, converted to rem
// (see `SectionOrg.settings`). Kept as literal class strings — Tailwind scans
// source text, so these cannot be computed from `spacingSpace` at runtime.
export const NEGATIVE_MARGIN_TOP_CLASS: Record<SpacingBase, string> = {
  // 128 / 128 / 240px
  giant: "-mt-[8rem] md:-mt-[8rem] lg:-mt-[15rem]",
  // 64 / 96 / 240px
  huge: "-mt-[4rem] md:-mt-[6rem] lg:-mt-[15rem]",
  // 64 / 96 / 200px
  large: "-mt-[4rem] md:-mt-[6rem] lg:-mt-[12.5rem]",
  // 64 / 96 / 120px
  big: "-mt-[4rem] md:-mt-[6rem] lg:-mt-[7.5rem]",
  // 48 / 64 / 80px
  default: "-mt-[3rem] md:-mt-[4rem] lg:-mt-[5rem]",
  // 24 / 32 / 40px
  small: "-mt-[1.5rem] md:-mt-[2rem] lg:-mt-[2.5rem]",
  // 24 / 24 / 24px
  extraSmall: "-mt-[1.5rem] md:-mt-[1.5rem] lg:-mt-[1.5rem]",
  sticky: "",
}

export const NEGATIVE_MARGIN_BOTTOM_CLASS: Record<SpacingBase, string> = {
  giant: "-mb-[8rem] md:-mb-[8rem] lg:-mb-[15rem]",
  huge: "-mb-[4rem] md:-mb-[6rem] lg:-mb-[15rem]",
  large: "-mb-[4rem] md:-mb-[6rem] lg:-mb-[12.5rem]",
  big: "-mb-[4rem] md:-mb-[6rem] lg:-mb-[7.5rem]",
  default: "-mb-[3rem] md:-mb-[4rem] lg:-mb-[5rem]",
  small: "-mb-[1.5rem] md:-mb-[2rem] lg:-mb-[2.5rem]",
  extraSmall: "-mb-[1.5rem] md:-mb-[1.5rem] lg:-mb-[1.5rem]",
  sticky: "",
}
